import { Badge } from "@thilke/ui/components/badge";
import { Button } from "@thilke/ui/components/button";
import { Panel } from "@thilke/ui/components/panel";
import {
  Status,
  StatusIndicator,
  StatusLabel,
} from "@thilke/ui/components/ui/status";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface PartyMember {
  id: string;
  name: string;
  role?: string;
  hp?: number;
  maxHp?: number;
  status?: "ready" | "active" | "down" | "offline";
  initiative?: number | string;
}

export interface PartyTrackerProps extends React.ComponentProps<"div"> {
  members: PartyMember[];
  title?: string;
  action?: React.ReactNode;
}

const memberStatusVariant: Record<
  NonNullable<PartyMember["status"]>,
  React.ComponentProps<typeof Status>["variant"]
> = {
  ready: "success",
  active: "info",
  down: "error",
  offline: "default",
};

function PartyTracker({
  members,
  title = "Party",
  action,
  className,
  ...props
}: PartyTrackerProps) {
  return (
    <Panel
      position="docked"
      size="md"
      className={cn("grid gap-2.5", className)}
      {...props}
    >
      <header className="flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold">{title}</h2>
          <Badge variant="outline">{members.length}</Badge>
        </div>
        {action ?? (
          <Button variant="ghost" size="sm">
            sync
          </Button>
        )}
      </header>
      <div className="grid gap-1.5">
        {members.map((member) => {
          const hpLabel =
            member.hp !== undefined
              ? `${member.hp}${member.maxHp ? `/${member.maxHp}` : ""}`
              : "--";
          const status = member.status ?? "ready";
          return (
            <div
              key={member.id}
              className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-md border border-border bg-card/80 px-3 py-2 text-sm"
            >
              <div className="min-w-0">
                <div className="truncate font-medium text-foreground">
                  {member.name}
                </div>
                {member.role ? (
                  <div className="text-xs text-muted-foreground">
                    {member.role}
                  </div>
                ) : null}
              </div>
              <Status variant={memberStatusVariant[status]}>
                <StatusIndicator />
                <StatusLabel>{status}</StatusLabel>
              </Status>
              <div className="text-right font-mono text-xs font-medium text-foreground/85">
                <div>hp {hpLabel}</div>
                {member.initiative !== undefined ? (
                  <div>init {member.initiative}</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

export { PartyTracker };
